import {
  addDays,
  addHours,
  addMonths,
  differenceInHours,
  endOfDay,
  endOfMonth,
  setHours,
  setMinutes,
  startOfDay,
  startOfMonth,
  startOfToday,
} from 'date-fns';
import { CAPTAIN_OPTION_1_INCLUDED_IN_PRICE, CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN } from 'src/common/constants';
import { DurationUnit } from 'src/common/types/api/listings';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import { CaptainOptionValue } from 'src/common/types/CaptainOptions';
import newTZDate, { formatDate } from 'src/common/utils/dateHelpers';
import getPaginatedInstabooks from 'src/common/utils/getPaginatedInstabooks';
import { FULL_DAY_HOURS } from 'src/listing/constants';
import {
  getDurationHours,
  getMinMaxHoursForPreferredPriceTier,
  getUniqueTripListByDuration,
  isOnlyMultiDay,
  UnitPriceDetail,
} from 'src/listing/helper';
import getInstabookDates from 'src/trip-cart-deprecated/utils/getInstabookDates';
import { getCachedInstabookOnDate, updateCachedInstabooksOnDate } from 'src/trip-cart-deprecated/utils/tripCartCache';

export const getInstabookDatesForMonth = async (
  listingId: string,
  date: Date,
): Promise<string[]> => {
  const formattedDate = formatDate(date);
  const dates = await getInstabookDates(listingId, formattedDate, formatDate(endOfMonth(date)));
  return dates;
};

/**
   * Get current, previous, and next month's instabooks.
   * Will ignore previous month if it's in the past
   * cachedMonths will be modified
   */
export const getInstabookDatesAroundMonth = async (
  listingId: string,
  fromMonth: Date,
  cachedMonths: Set<string>,
): Promise<string[]> => {
  const minimumMonth = startOfMonth(startOfToday());
  const currentMonth = startOfMonth(fromMonth);

  // Load previous, current, and next month if not already loaded
  // Only load previous month if it's not before today's month
  const monthOffsets = [-1, 0, 1];

  const groups = await Promise.all(monthOffsets.map(async (offset) => {
    const month = addMonths(currentMonth, offset);
    const key = formatDate(month);
    if (!cachedMonths.has(key) && month >= minimumMonth) {
      const results = await getInstabookDatesForMonth(listingId, month);
      // Note: modifies cache
      cachedMonths.add(key);
      return results;
    }
    return [];
  }));
  const newDates = groups.flat(Infinity) as string[];

  return newDates;
};

export const getClosestInstabookDate = (
  fromDate: Date,
  instabookDates: string[],
): Date => {
  // Get closest date, current selected date has no instabook
  // Start is ignored, will be overwritten
  let closestDate: Date = fromDate;
  let closestDifference = Infinity;
  // find closest date
  for (const dateStr of instabookDates) {
    const date = newTZDate(dateStr);
    const difference = Math.abs(
      Math.ceil(
        differenceInHours(date, fromDate) / FULL_DAY_HOURS,
      ),
    );
    if (difference < closestDifference) {
      closestDifference = difference;
      closestDate = date;
    }
  }
  return closestDate;
};

export const checkInstabookTripsForMultiDays = (
  listing: FullBoatSearchDetail,
  instabookDates: string[],
): boolean => (
  listing.has_available_instabook
  && isOnlyMultiDay(listing.prices)
  && instabookDates.length > 0
);

let callIdGetInstabookDurationsOnDate = 0;

/**
 * Get trips and durations. Will be cancelled if a new request is made.
 * If the call is cancelled, it will returns null.
 */
export const getInstabookAvailabilitiesOnDate = async (
  listingId: string,
  date: Date,
  guests: number,
  currencyCode: string,
  ignoreCache: boolean = false,
): Promise<{
  availabilities: InstabookAvailability[];
  durations: number[];
} | null> => {
  // Subsequent calls will cause this to be cancelled and discard results
  callIdGetInstabookDurationsOnDate += 1;
  const callId = callIdGetInstabookDurationsOnDate;

  const formattedDate = formatDate(date);

  const searchParams = {
    listingId,
    startDate: formattedDate,
    endDate: formattedDate,
    capacity: `${guests}`,
    currency: currencyCode,
  };
  const cache = getCachedInstabookOnDate(searchParams);

  // Hard caching. Only cleared on page reload
  if (!ignoreCache && cache) {
    return cache;
  }

  const pagination = getPaginatedInstabooks(searchParams);

  let availabilities;

  for (;;) {
    // Cannot parallelize pagination, until second call
    // TODO: parallelize 2nd call onwards, only when bulk instabooks are
    // more often greater than PAGE_SIZE*2.
    // eslint-disable-next-line no-await-in-loop
    const page = await pagination.next();

    // Since `page` is awaited, there might have been another call to
    // `getAndUpdateInstabookDurationsOnDate` in the meantime. Abort this
    // process if that happens, as the other call has more recent parameters
    if (callIdGetInstabookDurationsOnDate !== callId) {
      return null;
    }

    // Only the final value will contain all trips
    if (page.done) {
      availabilities = page.value;
      break;
    }
  }

  // Only keep unique durations. Already sorted
  const durations = getUniqueTripListByDuration(availabilities);
  updateCachedInstabooksOnDate(searchParams, {
    availabilities,
    durations,
  });
  return {
    availabilities,
    durations,
  };
};

export type TripCartDurationOption = {
  duration: number;
  unit: DurationUnit;
  isInstabook: boolean;
  durationFormatted: string; // in PT{number}H
};

export const formatDurationUnit = (
  duration: number,
  unit: DurationUnit,
): string => {
  if (unit === 'hour') {
    return `PT${duration}H`;
  }

  // Night is formatted the same as day for API, but still represents an extra day
  return `PT${duration * FULL_DAY_HOURS}H`;
};

const getOption = (
  duration: number,
  unit: DurationUnit,
): TripCartDurationOption => ({
  duration,
  unit,
  isInstabook: false,
  durationFormatted: formatDurationUnit(duration, unit),
});

const sortOptions = (a: TripCartDurationOption, b: TripCartDurationOption) => {
  // Ensure nights and days are sorted to the end. Nights and days will
  // never be in the same list so it's not important to multiply by
  // 48 for nights
  const aValue = a.duration * (a.unit !== 'hour' ? FULL_DAY_HOURS : 1);
  const bValue = b.duration * (b.unit !== 'hour' ? FULL_DAY_HOURS : 1);
  return aValue - bValue;
};

// Only use this for same day ('hour' durations)
export const mergeDurationInstabookOptions = (
  options: TripCartDurationOption[],
  instabookDurations: number[],
): TripCartDurationOption[] => {
  const instabookOptions = instabookDurations.map((duration) => {
    const option = getOption(duration, 'hour');
    option.isInstabook = true;
    return option;
  });

  const nonInstabookOptions = options.filter((option) => !(option.unit === 'hour'
    && instabookDurations.includes(option.duration)));

  const merged = [
    ...instabookOptions,
    ...nonInstabookOptions,
  ].sort(sortOptions);

  return merged;
};

/**
 * Always show full day on same day i.e. move the multiday duration over to same day
 */
export const hasFullDay = (prices: UnitPriceDetail): boolean => {
  if (!prices.multiday?.length) return false;

  return prices.multiday.some((tier) => tier.price_unit === 'day' && tier.min_unit_quantity === 1);
};

// Similar to listing/helper#getDurationOptions, but does not do translations
export const getDurationOptions = (
  unitPriceDetails: UnitPriceDetail,
  isMultiDay: boolean,
): TripCartDurationOption[] => {
  const tierType: keyof UnitPriceDetail = isMultiDay ? 'multiday' : 'hour';
  let tiers = unitPriceDetails?.[tierType];

  if (!tiers) {
    // Invalid listing
    return [];
  }

  const includesFullDay = hasFullDay(unitPriceDetails);
  // Manually set the tier data for 'hour' as that does not exist on unitPriceDetails
  // when pricing is only in days. We want to move '1 day' to hourly duration option.
  // The remaining days will be kept multiday
  if (!isMultiDay && includesFullDay && !tiers.length) {
    // Will exist due to above check `hasFullDay`
    const dayItem = unitPriceDetails.multiday![0];

    tiers = [{
      currency: dayItem.currency,
      min_unit_quantity: FULL_DAY_HOURS,
      max_unit_quantity: FULL_DAY_HOURS,
      price_unit: 'hour',
      value: dayItem.value,
    }];
  }

  const minMaxHours = getMinMaxHoursForPreferredPriceTier(tiers);

  // Config error
  if (!minMaxHours) return [];

  const { min: minHours, max: maxHours } = minMaxHours;
  const hourDivisor = tierType === 'multiday' ? FULL_DAY_HOURS : 1;
  const count = (maxHours - minHours) / hourDivisor + 1;
  const min = minHours / hourDivisor;
  const unit = tiers[0].price_unit as DurationUnit;

  const options: TripCartDurationOption[] = Array.from({ length: count }, (_, i) => {
    const duration = i + min;
    return getOption(duration, unit);
  });

  const hasMixedPricing = unitPriceDetails && (
    (
      unitPriceDetails.hour?.length
      && unitPriceDetails.multiday?.length
    )
    || includesFullDay
  );

  if (!hasMixedPricing) {
    return options;
  }

  if (isMultiDay) {
    // Remove "1 day" option for multiday
    if (unit === 'day' && includesFullDay) {
      options.shift();
    }
    return options;
  }

  // Same day. Add "Full day" option
  if (includesFullDay) {
    const lastIndex = options.findLastIndex((option) => option.duration === FULL_DAY_HOURS);
    if (lastIndex === options.length - 1) {
      // Remove option configured from '24 hours' and then reset to 1 day
      options.pop();
    }
    // '1 Day' option is removed from multi-day array,
    // it's safe to add option to hours array
    options.push(getOption(1, 'day'));
  }
  return options;
};

// Similar to TripDetailsOld#calculateUpdatedDateRange
export const getDateRangeForDuration = (
  startDate: Date,
  duration: number,
  unit: DurationUnit,
): Date[] => {
  if (unit === 'hour') return [startDate];

  // Will add a full day
  let days = duration;

  // Nights extend by a day, e.g. one night is 2 days. One day has same start/end date
  if (unit === 'day') {
    days -= 1;
  }

  // if days is zero or less than zero, return the start date only.
  if (days <= 0) {
    return [startDate];
  }

  const end = addDays(startDate, days);
  // update end date to match duration
  return [startOfDay(startDate), endOfDay(end)];
};

export const getInstabookTrip = (
  instabooks: InstabookAvailability[],
  duration: string,
  captainValue: CaptainOptionValue,
): InstabookAvailability | null => {
  if (!instabooks.length) return null;
  const isCaptained = Number(captainValue) === CAPTAIN_OPTION_1_INCLUDED_IN_PRICE
  || Number(captainValue) === CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN;

  const durationValue = getDurationHours(duration);
  const trip = instabooks.find((instabook) => {
    const instabookDuration = Number(instabook.trip_length.split(':')[0]);
    return instabook.captained === isCaptained && durationValue === instabookDuration;
  }) || null;

  return trip;
};

/**
 *  24 hours, return [hours, minutes] e.g. 11:30 returns [11,30].
 */
export const getHoursAndMinutesFromTime = (time: string): [number, number] | undefined => {
  if (!time) {
    return undefined;
  }

  const [hoursStr, minutesStr] = time.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  // Detect invalid formats
  if (!hoursStr || !minutesStr) {
    return undefined;
  }
  if (hours < 0 || hours > 23 || hoursStr.length !== 2) {
    return undefined;
  }
  if (minutes < 0 || minutes > 59 || minutesStr.length !== 2) {
    return undefined;
  }

  if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
    return [hours, minutes];
  }
  return undefined;
};

/**
 * Set the date to start at departure time and adds duration
 */
export const getReturnDateFromDurationAndDepartureTime = (
  date: Date,
  /** format PT{number}H * */
  duration: string,
  /** format 24 hour time e.g. 23:45 * */
  departureTime: string,
): Date => {
  const timeValue = getHoursAndMinutesFromTime(departureTime);
  let initialDate = new Date(date);
  if (timeValue) {
    const [hours, minutes] = timeValue;
    initialDate = setHours(initialDate, hours);
    initialDate = setMinutes(initialDate, minutes);
  }

  const returnDate = addHours(initialDate, getDurationHours(duration));
  return returnDate;
};
